import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { EMPTY_BILL_INDEX, fetchBooksPayoutBills, liveBillFor, type BillIndex } from "../../lib/booksPayoutBills";
import { supabase } from "../../lib/supabase";
import { fetchCanvasserByDeal } from "../../lib/businessSnapshot";
import { mergeRates, ratesMapFromRows } from "../../lib/commissionRates";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { fmtUSD, fmtInt } from "../../lib/analytics";
import type { TeamMember } from "../../lib/types";
import DealLink from "../DealLink";
import DrawRequestModal from "../draws/DrawRequestModal";
import PaidProvenanceChip from "../commissions/PaidProvenanceChip";
import {
  EMPTY_STORED,
  fetchCommissionPayments,
  fetchMemberNames,
  indexPayments,
  payeeIdsNotIn,
  type CommissionPaymentRow,
  type StoredPayments,
} from "../../lib/commissionPayments";
import {
  groupByPerson,
  groupByPersonStored,
  COMMISSION_DEAL_SELECT,
  COMM_TYPE_LABELS,
  COMM_TYPE_COLORS,
  type CommissionDeal,
  type CanvasserRef,
  type PersonGroup,
  type CommTypeGroup,
  type CommLineItem,
} from "../commissions/CommissionBoard";

/**
 * Self-service commission board (My Pipeline → Commissions tab) for RMs, Team
 * Leads, and ASMs. READ-ONLY — Chad/finance still own approvals.
 *
 * Shows the logged-in person, for each status bucket (Pending / Approved / Paid):
 *   • Personal commission — their own RM deals (full + split), per deal.
 *   • Override — TL 1% / ASM 4% on their team's deals, per deal.
 *   • Team rollup (TL/ASM only) — each team member's personal commission total.
 *
 * The math is the SAME groupByPerson() / scopeCalc() the finance CommissionBoard
 * uses, so the numbers here always reconcile with what Chad and Shannon see.
 * Bucketing mirrors the board: by the commission_approved / commission_paid
 * boolean flags, not by stage.
 */

type Bucket = "pending" | "approved" | "paid";

// "Personal" money = the lines a person earns on deals they hold — their RM
// line, their split half, (Sep 25, 2026) a stored 5★ true-up on top of it, and
// (Oct 29, 2026) the trade upside pool. A trade_upside line is personal on a
// deal the viewer holds (owner or split RM) and an override when Chad gave a
// TL / ASM a share of someone else's pool — see splitUpsideByDeal.
const PERSONAL_TYPES = new Set<string>(["rm", "rm_split", "trueup", "trade_upside"]);

/** Oct 29, 2026 — a person's trade_upside group split into the deals they hold (personal) and the rest (an
 *  override Chad carved them). Other types pass through: PERSONAL_TYPES decides them whole. */
function splitUpsideByDeal(groups: CommTypeGroup[], personId: string): { personal: CommTypeGroup[]; override: CommTypeGroup[] } {
  const personal: CommTypeGroup[] = [];
  const override: CommTypeGroup[] = [];
  for (const g of groups) {
    if (g.type !== "trade_upside") {
      if (PERSONAL_TYPES.has(g.type)) personal.push(g);
      else if (g.type === "tl_override" || g.type === "asm_override") override.push(g);
      continue;
    }
    const mine = g.items.filter((it) => it.deal.owner_id === personId || it.deal.split_rm_id === personId);
    const theirs = g.items.filter((it) => !(it.deal.owner_id === personId || it.deal.split_rm_id === personId));
    if (mine.length) personal.push({ ...g, items: mine, total: mine.reduce((s, i) => s + i.amount, 0) });
    if (theirs.length) override.push({ ...g, items: theirs, total: theirs.reduce((s, i) => s + i.amount, 0) });
  }
  return { personal, override };
}

const BUCKETS: Array<{ key: Bucket; label: string; hint: string }> = [
  { key: "pending", label: "Pending", hint: "Awaiting Chad's approval" },
  { key: "approved", label: "Approved", hint: "Approved — awaiting payout" },
  { key: "paid", label: "Paid", hint: "Paid out" },
];

function firstName(name: string | undefined | null): string {
  return (name ?? "").split(" ")[0] || (name ?? "");
}

// The date a deal "counts" on for the custom date filter — bucket-appropriate:
// paid → paid date, approved → approved date, else commission-ready date.
function anchorDay(d: CommissionDeal): string | null {
  const raw = d.commission_paid
    ? (d.commission_paid_at ?? d.commission_ready_date)
    : d.commission_approved
      ? (d.commission_approved_at ?? d.commission_ready_date)
      : d.commission_ready_date;
  return raw ? String(raw).slice(0, 10) : null;
}

type ExecEntry = { deal: CommissionDeal; total: number; lines: { personId: string; personName: string; item: CommLineItem }[] };

export default function MyCommissionsTab({ member, isExec = false }: { member: TeamMember; isExec?: boolean }) {
  const myId = member.id;
  const isTL = member.role === "team_lead";
  const isASM = member.role === "asm";

  const [deals, setDeals] = useState<CommissionDeal[] | null>(null);
  const [members, setMembers] = useState<Record<string, TeamMember>>({});
  const [canvasserByDeal, setCanvasserByDeal] = useState<Record<string, CanvasserRef>>({});
  // Sep 25, 2026 — the stored payout ledger, RLS-scoped to what this viewer may
  // see (own lines; a TL/ASM also sees their downline's). The Paid bucket
  // renders these rows; only pre-ledger payouts are still recomputed.
  const [storedPayments, setStoredPayments] = useState<StoredPayments>(EMPTY_STORED);
  const [payeeNames, setPayeeNames] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [bucket, setBucket] = useState<Bucket>("pending");
  // Custom date-range filter (yyyy-mm-dd; "" = unbounded on that side).
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  // Exec-only cascading owner filters (ASM → TL → RM).
  const [filterASM, setFilterASM] = useState<string>("all");
  const [filterTL, setFilterTL] = useState<string>("all");
  const [filterRM, setFilterRM] = useState<string>("all");
  // Bumped by realtime subscriptions to re-pull data so overrides stay correct.
  const [reloadKey, setReloadKey] = useState(0);

  // Draw banner (same data the RM commissions tab uses).
  const [drawBalance, setDrawBalance] = useState(0);
  const [pendingDrawCount, setPendingDrawCount] = useState(0);
  const [showDrawModal, setShowDrawModal] = useState(false);

  // Live subscriptions. team_roster drives override attribution (which RM rolls
  // up to which TL/ASM) — added to the supabase_realtime publication Jun 29,
  // 2026 so this isn't a silent no-op. commission_draws/repayments keep the draw
  // banner fresh. We deliberately do NOT subscribe to `deals` (whole-table churn
  // is far too noisy — matches the finance CommissionBoard's choice); the tab
  // re-pulls deals fresh each time it's opened.
  const bump = () => setReloadKey((k) => k + 1);
  useRealtimeTable("team_roster", bump);
  useRealtimeTable("commission_draws", bump);
  useRealtimeTable("commission_draw_repayments", bump);
  useRealtimeTable("commission_payments", bump);
  useRealtimeTable("books_payout_bills", bump);

  // Oct 8, 2026 — my Books bills (RLS: a person reads their own): a deal in
  // the Approved bucket with a live commission bill reads "sent to Books for
  // payment". Best-effort: a failed read only hides the chip.
  const [myBills, setMyBills] = useState<BillIndex>(EMPTY_BILL_INDEX);
  useEffect(() => {
    let cancelled = false;
    fetchBooksPayoutBills({ memberId: myId, kinds: ["commission"] })
      .then((ix) => { if (!cancelled) setMyBills(ix); })
      .catch(() => { if (!cancelled) setMyBills(EMPTY_BILL_INDEX); });
    return () => { cancelled = true; };
  }, [myId, reloadKey]);
  const sentToBooks = useCallback(
    (dealId: string) => bucket === "approved" && !!liveBillFor(myBills, "deals", dealId, { memberId: myId, kind: "commission" }),
    [myBills, myId, bucket],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setErr(null);
      const [{ data: d, error: e1 }, { data: m, error: e2 }, { data: rates, error: e3 }, stored] = await Promise.all([
        supabase
          .from("deals")
          .select(COMMISSION_DEAL_SELECT)
          .not("commission_ready_date", "is", null)
          .order("commission_ready_date", { ascending: true }),
        supabase.from("team_roster").select("*").eq("is_active", true),
        // Real rates live in team_roster_comp (Sep 9, 2026); the RPC returns
        // what this viewer may see (self / team / everyone for leadership).
        // Fatal on error — a money surface must not silently fall to 10%.
        supabase.rpc("visible_commission_rates"),
        // Sep 25, 2026 — the stored payout ledger (RLS-scoped). Fatal on error:
        // paid history must never silently fall back to a recompute.
        fetchCommissionPayments()
          .then((rows) => ({ rows, error: null as { message: string } | null }))
          .catch((e: unknown) => ({ rows: [] as CommissionPaymentRow[], error: { message: e instanceof Error ? e.message : String(e) } })),
      ]);
      const error = e1 ?? e2 ?? e3 ?? stored.error;
      if (error) {
        if (!cancelled) setErr(error.message);
        return;
      }
      if (cancelled) return;

      setDeals((d ?? []) as CommissionDeal[]);
      const byId: Record<string, TeamMember> = {};
      (m ?? []).forEach((x) => { byId[x.id] = x as TeamMember; });
      mergeRates(byId, ratesMapFromRows(rates));
      setMembers(byId);
      const ledger = indexPayments(stored.rows);
      setStoredPayments(ledger);
      // Best-effort: a failed name lookup just renders "Former team member".
      const missingPayees = payeeIdsNotIn(ledger, byId);
      if (missingPayees.length > 0) {
        fetchMemberNames(missingPayees)
          .then((names) => { if (!cancelled) setPayeeNames(names); })
          .catch(() => { if (!cancelled) setPayeeNames({}); });
      } else {
        setPayeeNames({});
      }

      // Canvasser carve-out: the shared rule (lib/canvasserCredit) the board,
      // the My Business snapshot and this tab all read. A failed read shows
      // the error instead of lines that silently skip the carve-out.
      const dealIds = (d ?? []).map((x: { id: string }) => x.id);
      if (dealIds.length > 0) {
        try {
          const cmap = await fetchCanvasserByDeal(dealIds);
          if (!cancelled) setCanvasserByDeal(cmap);
        } catch (e) {
          if (!cancelled) setErr(e instanceof Error ? e.message : String((e as { message?: string })?.message ?? e));
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [myId, reloadKey]);

  // Outstanding draw balance + pending request count for this member.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [drawsRes, repaysRes] = await Promise.all([
        supabase.from("commission_draws").select("id, approved_amount, paid_at, approval_status").eq("team_member_id", myId),
        supabase.from("commission_draw_repayments").select("draw_id, amount"),
      ]);
      if (cancelled) return;
      const myDraws = (drawsRes.data ?? []) as Array<{ id: string; approved_amount: number | null; paid_at: string | null; approval_status: string }>;
      const myDrawIds = new Set(myDraws.map((d) => d.id));
      const repaidByDraw: Record<string, number> = {};
      for (const r of (repaysRes.data ?? []) as Array<{ draw_id: string; amount: number }>) {
        if (myDrawIds.has(r.draw_id)) repaidByDraw[r.draw_id] = (repaidByDraw[r.draw_id] || 0) + Number(r.amount);
      }
      const balance = myDraws
        .filter((d) => d.approval_status === "approved" && d.paid_at)
        .reduce((s, d) => s + (Number(d.approved_amount) || 0) - (repaidByDraw[d.id] || 0), 0);
      setDrawBalance(Math.max(0, balance));
      setPendingDrawCount(myDraws.filter((d) => d.approval_status === "pending").length);
    })();
    return () => { cancelled = true; };
  }, [myId, reloadKey]);

  // Bucket the deals by the boolean flags (mirrors CommissionBoard exactly),
  // after applying the custom date-range filter.
  const buckets = useMemo(() => {
    const pending: CommissionDeal[] = [];
    const approved: CommissionDeal[] = [];
    const paid: CommissionDeal[] = [];
    for (const d of deals ?? []) {
      if (fromDate || toDate) {
        const day = anchorDay(d);
        if (!day) continue;
        if (fromDate && day < fromDate) continue;
        if (toDate && day > toDate) continue;
      }
      if (d.commission_paid) paid.push(d);
      else if (d.commission_approved) approved.push(d);
      else pending.push(d);
    }
    return { pending, approved, paid };
  }, [deals, fromDate, toDate]);

  // The team members whose personal commission rolls up under me.
  // TL → their RMs (team_lead_id points at me). ASM → RMs (asm_id points at me)
  // plus the TLs reporting to me (team_lead_id points at me). Exclude myself.
  const teamIds = useMemo(() => {
    if (!isTL && !isASM) return [] as string[];
    const ids = new Set<string>();
    for (const m of Object.values(members)) {
      if (m.id === myId) continue;
      if (m.team_lead_id === myId) ids.add(m.id);
      if (isASM && m.asm_id === myId) ids.add(m.id);
    }
    return [...ids];
  }, [members, myId, isTL, isASM]);

  // groupByPerson once per bucket — reused by the self view, exec inversion, and
  // the pill totals, so the active bucket is never grouped twice per render.
  const groupsByBucket = useMemo(() => ({
    pending: groupByPerson(buckets.pending, members, canvasserByDeal),
    approved: groupByPerson(buckets.approved, members, canvasserByDeal),
    // Paid = the stored ledger where it exists (Sep 25, 2026); live recompute
    // only for payouts that predate it.
    paid: groupByPersonStored(buckets.paid, members, canvasserByDeal, storedPayments, payeeNames),
  }), [buckets, members, canvasserByDeal, storedPayments, payeeNames]);

  // Active bucket → pick my group + team rollup.
  const view = useMemo(() => {
    const groups = groupsByBucket[bucket];
    const byId: Record<string, PersonGroup> = {};
    for (const g of groups) byId[g.personId] = g;

    const mine = byId[myId];
    const { personal: personalGroups, override: overrideGroups } = splitUpsideByDeal(mine?.typeGroups ?? [], myId);
    const personalTotal = personalGroups.reduce((s, t) => s + t.total, 0);
    const overrideTotal = overrideGroups.reduce((s, t) => s + t.total, 0);

    // Override organized by RM → each job under them (what James asked for).
    // On a split deal where the viewer is the split RM, the override item's
    // deal.owner_id is the OTHER RM, so it correctly groups under that RM.
    const ovByRm = new Map<string, { rmId: string; rmName: string; total: number; items: CommLineItem[] }>();
    for (const g of overrideGroups) {
      for (const it of g.items) {
        const rmId = it.deal.owner_id ?? "unknown";
        const rmName = it.deal.owner_id ? (members[it.deal.owner_id]?.full_name ?? "—") : "—";
        const e = ovByRm.get(rmId) ?? { rmId, rmName, total: 0, items: [] };
        e.total += it.amount;
        e.items.push(it);
        ovByRm.set(rmId, e);
      }
    }
    const overrideByRm = [...ovByRm.values()]
      .map((r) => ({ ...r, items: r.items.sort((a, b) => b.amount - a.amount) }))
      .sort((a, b) => b.total - a.total);

    const teamRows = teamIds
      .map((id) => {
        const g = byId[id];
        const theirPersonal = splitUpsideByDeal(g?.typeGroups ?? [], id).personal;
        const personal = theirPersonal.reduce((s, t) => s + t.total, 0);
        const deals = new Set(theirPersonal.flatMap((t) => t.items.map((i) => i.deal.id))).size;
        return { id, name: members[id]?.full_name ?? "—", role: members[id]?.role, personal, deals };
      })
      .filter((r) => r.personal > 0)
      .sort((a, b) => b.personal - a.personal);

    return {
      personalGroups,
      overrideByRm,
      personalTotal,
      overrideTotal,
      myTotal: (mine?.totalComm ?? 0),
      teamRows,
      teamTotal: teamRows.reduce((s, r) => s + r.personal, 0),
    };
  }, [groupsByBucket, bucket, members, myId, teamIds]);

  // Exec/admin: company-wide per-deal breakdown per bucket. Invert groupByPerson
  // into one entry per deal, listing every commission line (RM / RM split / TL
  // override / ASM override) with who earns it + the fee breakdown.
  const execEntries = useMemo(() => {
    const empty = { pending: [] as ExecEntry[], approved: [] as ExecEntry[], paid: [] as ExecEntry[] };
    if (!isExec) return empty;
    const order: Record<string, number> = { rm: 0, rm_split: 1, trade_upside: 2, tl_override: 3, asm_override: 4, trueup: 5 };
    const build = (groups: PersonGroup[]): ExecEntry[] => {
      const byDeal = new Map<string, ExecEntry>();
      for (const g of groups) {
        for (const tg of g.typeGroups) {
          for (const it of tg.items) {
            const e = byDeal.get(it.deal.id) ?? { deal: it.deal, total: 0, lines: [] };
            e.total += it.amount;
            e.lines.push({ personId: g.personId, personName: g.name, item: it });
            byDeal.set(it.deal.id, e);
          }
        }
      }
      return [...byDeal.values()]
        .map((e) => ({ ...e, lines: e.lines.sort((a, b) => (order[a.item.type] ?? 9) - (order[b.item.type] ?? 9)) }))
        .sort((a, b) => b.total - a.total);
    };
    return { pending: build(groupsByBucket.pending), approved: build(groupsByBucket.approved), paid: build(groupsByBucket.paid) };
  }, [isExec, groupsByBucket]);

  // Roster groupings + dropdown options for the exec ASM→TL→RM filter.
  const rosterGroups = useMemo(() => {
    const all = Object.values(members);
    return {
      asms: all.filter((m) => m.role === "asm"),
      tls: all.filter((m) => m.role === "team_lead"),
      rms: all.filter((m) => m.role === "restore_manager"),
    };
  }, [members]);

  const filterOpts = useMemo(() => {
    const { asms, tls, rms } = rosterGroups;
    const visibleTLs = filterASM === "all" ? tls : tls.filter((t) => t.team_lead_id === filterASM);
    let visibleRMs: TeamMember[];
    if (filterTL !== "all") {
      const tl = tls.find((t) => t.id === filterTL);
      visibleRMs = [...(tl ? [tl] : []), ...rms.filter((r) => r.team_lead_id === filterTL)];
    } else if (filterASM !== "all") {
      const asmTLs = tls.filter((t) => t.team_lead_id === filterASM);
      const asm = asms.find((a) => a.id === filterASM);
      visibleRMs = [...(asm ? [asm] : []), ...asmTLs, ...rms.filter((r) => asmTLs.some((t) => t.id === r.team_lead_id))];
    } else {
      visibleRMs = [...asms, ...tls, ...rms];
    }
    return { asms, visibleTLs, visibleRMs };
  }, [rosterGroups, filterASM, filterTL]);

  // Owner-id set the exec filter allows (deal shows if owner OR split is in it).
  const allowedOwners = useMemo(() => {
    const { tls, rms } = rosterGroups;
    if (filterRM !== "all") return new Set([filterRM]);
    if (filterTL !== "all") return new Set([filterTL, ...rms.filter((r) => r.team_lead_id === filterTL).map((r) => r.id)]);
    if (filterASM !== "all") {
      const asmTLs = tls.filter((t) => t.team_lead_id === filterASM);
      return new Set([filterASM, ...asmTLs.map((t) => t.id), ...rms.filter((r) => asmTLs.some((t) => t.id === r.team_lead_id)).map((r) => r.id)]);
    }
    return null;
  }, [rosterGroups, filterASM, filterTL, filterRM]);

  const filteredExec = useMemo(() => {
    const f = (arr: ExecEntry[]) =>
      !allowedOwners
        ? arr
        : arr.filter((e) => (e.deal.owner_id && allowedOwners.has(e.deal.owner_id)) || (e.deal.split_rm_id && allowedOwners.has(e.deal.split_rm_id)));
    return { pending: f(execEntries.pending), approved: f(execEntries.approved), paid: f(execEntries.paid) };
  }, [execEntries, allowedOwners]);

  const execDeals = filteredExec[bucket];

  // Per-bucket total for the pill badges — company-wide (filtered) for exec, else mine.
  const bucketTotals = useMemo(() => {
    const out: Record<Bucket, number> = { pending: 0, approved: 0, paid: 0 };
    (Object.keys(out) as Bucket[]).forEach((b) => {
      if (isExec) {
        out[b] = filteredExec[b].reduce((s, e) => s + e.total, 0);
      } else {
        out[b] = groupsByBucket[b].find((x) => x.personId === myId)?.totalComm ?? 0;
      }
    });
    return out;
  }, [isExec, filteredExec, groupsByBucket, myId]);

  if (err) return <div className="p-4 bg-red-50 text-red-700 rounded">Couldn't load commissions — {err}</div>;
  if (!deals) return <div className="p-8 text-slate-500">Loading commissions…</div>;

  return (
    <div>
      {/* Draw balance banner (personal — hidden for exec/admin) */}
      {!isExec && (
      <div className="bg-gradient-to-r from-purple-50 to-white border border-purple-200 rounded-xl p-4 mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-purple-700">Outstanding draw balance</div>
          <div className="text-2xl font-extrabold text-purple-700 mt-1">{fmtUSD(drawBalance)}</div>
          <div className="text-[11px] text-slate-600 mt-0.5">
            {drawBalance > 0
              ? "Auto-deducted from your next commission payouts (FIFO)."
              : "No outstanding draws."}
            {pendingDrawCount > 0 && <> · {pendingDrawCount} request awaiting Chad's approval</>}
          </div>
        </div>
        <button
          onClick={() => setShowDrawModal(true)}
          className="px-4 py-2 rounded-lg bg-bardin-orange text-white text-sm font-medium hover:opacity-90"
        >
          💵 Request a draw
        </button>
      </div>
      )}

      {showDrawModal && (
        <DrawRequestModal
          currentBalance={drawBalance}
          requesterName={member.full_name}
          requesterRole={member.role}
          onClose={() => setShowDrawModal(false)}
          onCreated={() => setPendingDrawCount((p) => p + 1)}
        />
      )}

      {/* Bucket pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {BUCKETS.map((b) => (
          <button
            key={b.key}
            onClick={() => setBucket(b.key)}
            className={
              "px-3 py-2 rounded-lg text-left border transition " +
              (bucket === b.key
                ? "border-bardin-teal bg-bardin-teal/5"
                : "border-slate-200 bg-white hover:border-slate-300")
            }
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-bardin-ink">{b.label}</span>
              <span className="text-sm font-bold text-bardin-teal">{fmtUSD(bucketTotals[b.key])}</span>
            </div>
            <div className="text-[11px] text-slate-500">{b.hint}</div>
          </button>
        ))}
      </div>

      {/* Custom date-range filter */}
      <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
        <span className="text-xs font-medium text-slate-500 uppercase">Date range</span>
        <input
          type="date"
          value={fromDate}
          max={toDate || undefined}
          onChange={(e) => setFromDate(e.target.value)}
          className="px-2 py-1 rounded border border-slate-200 text-sm bg-white focus:ring-bardin-teal focus:border-bardin-teal"
          aria-label="From date"
        />
        <span className="text-slate-400">to</span>
        <input
          type="date"
          value={toDate}
          min={fromDate || undefined}
          onChange={(e) => setToDate(e.target.value)}
          className="px-2 py-1 rounded border border-slate-200 text-sm bg-white focus:ring-bardin-teal focus:border-bardin-teal"
          aria-label="To date"
        />
        {(fromDate || toDate) && (
          <button
            onClick={() => { setFromDate(""); setToDate(""); }}
            className="px-2 py-1 rounded bg-slate-100 text-slate-600 text-xs hover:bg-slate-200"
          >
            Clear
          </button>
        )}
        <span className="text-[11px] text-slate-400">
          {fromDate || toDate ? "Paid by payout date · approved/pending by ready date" : "All time"}
        </span>
      </div>

      {/* Exec/admin — company-wide per-deal breakdown (RM / TL / ASM + fees) */}
      {isExec && (
        <div className="mb-4">
          {/* ASM → TL → RM filters */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select
              value={filterASM}
              onChange={(e) => { setFilterASM(e.target.value); setFilterTL("all"); setFilterRM("all"); }}
              className="px-2 py-1 rounded border border-slate-200 text-sm bg-white focus:ring-bardin-teal focus:border-bardin-teal"
              aria-label="Filter by ASM"
            >
              <option value="all">All ASMs</option>
              {filterOpts.asms.map((a) => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
            <select
              value={filterTL}
              onChange={(e) => { setFilterTL(e.target.value); setFilterRM("all"); }}
              className="px-2 py-1 rounded border border-slate-200 text-sm bg-white focus:ring-bardin-teal focus:border-bardin-teal"
              aria-label="Filter by Team Lead"
            >
              <option value="all">All TLs</option>
              {filterOpts.visibleTLs.map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
            </select>
            <select
              value={filterRM}
              onChange={(e) => setFilterRM(e.target.value)}
              className="px-2 py-1 rounded border border-slate-200 text-sm bg-white focus:ring-bardin-teal focus:border-bardin-teal"
              aria-label="Filter by RM"
            >
              <option value="all">All RMs</option>
              {filterOpts.visibleRMs.map((m) => (
                <option key={m.id} value={m.id}>{m.full_name}{m.role === "asm" ? " (ASM)" : m.role === "team_lead" ? " (TL)" : ""}</option>
              ))}
            </select>
            {(filterASM !== "all" || filterTL !== "all" || filterRM !== "all") && (
              <button
                onClick={() => { setFilterASM("all"); setFilterTL("all"); setFilterRM("all"); }}
                className="px-2 py-1 rounded bg-slate-100 text-slate-600 text-xs hover:bg-slate-200"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="text-sm text-slate-500 mb-2">
            {fmtInt(execDeals.length)} deal{execDeals.length === 1 ? "" : "s"} ·{" "}
            {filterASM !== "all" || filterTL !== "all" || filterRM !== "all" ? "filtered" : "company"} total{" "}
            <span className="font-semibold text-slate-700">{fmtUSD(bucketTotals[bucket])}</span>
          </div>
          {execDeals.length === 0 ? (
            <Empty>No commissions in this bucket{fromDate || toDate ? " for this date range" : ""}.</Empty>
          ) : (
            execDeals.map((e) => <ExecDealCard key={e.deal.id} entry={e} />)
          )}
        </div>
      )}

      {/* My total for the selected bucket */}
      {!isExec && (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-slate-500">
            Your total — <span className="font-medium text-slate-700">{BUCKETS.find((b) => b.key === bucket)!.label}</span>
          </div>
          <div className="text-2xl font-extrabold text-bardin-ink">{fmtUSD(view.myTotal)}</div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-xs text-slate-500">
          <span>Personal commission: <span className="font-semibold text-slate-700">{fmtUSD(view.personalTotal)}</span></span>
          {(isTL || isASM) && (
            <span>{isASM ? "ASM" : "TL"} override: <span className="font-semibold text-slate-700">{fmtUSD(view.overrideTotal)}</span></span>
          )}
        </div>
      </div>
      )}

      {/* Personal commission breakdown */}
      {!isExec && (
      <Section title="Your personal commission" subtitle="Deals you own (full + split), the trade upside pool included" total={view.personalTotal}>
        {view.personalGroups.length === 0 ? (
          <Empty>No personal commission in this bucket.</Empty>
        ) : (
          view.personalGroups.map((g) => (
            <TypeGroupBlock key={g.type} group={g} members={members} showOwner={false} sentToBooks={sentToBooks} />
          ))
        )}
      </Section>
      )}

      {/* Override breakdown — grouped by RM, then each job. TL/ASM only. */}
      {!isExec && (isTL || isASM) && (
        <Section
          title={`Your ${isASM ? "ASM" : "team-lead"} override`}
          subtitle={(isASM ? "4% on each RM's deals" : "1% on each RM's deals") + " — and any trade upside Chad shared with you"}
          total={view.overrideTotal}
        >
          {view.overrideByRm.length === 0 ? (
            <Empty>No override in this bucket.</Empty>
          ) : (
            <div className="divide-y divide-slate-100">
              {view.overrideByRm.map((rm) => (
                <div key={rm.rmId} className="px-4 py-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-semibold text-bardin-ink">
                      {rm.rmName}
                      <span className="ml-2 text-xs font-normal text-slate-400">{fmtInt(rm.items.length)} job{rm.items.length === 1 ? "" : "s"}</span>
                    </div>
                    <div className="text-sm font-bold text-amber-700">{fmtUSD(rm.total)}</div>
                  </div>
                  <ul className="divide-y divide-slate-50 pl-1">
                    {rm.items.map((it, idx) => (
                      <CommissionLine key={it.deal.id + ":" + idx} item={it} sentToBooks={sentToBooks(it.deal.id)} />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Team rollup — TL/ASM only */}
      {!isExec && (isTL || isASM) && (
        <details className="bg-white rounded-xl shadow-sm border border-slate-200 mb-4" open>
          <summary className="cursor-pointer select-none px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-bardin-ink">Team rollup — personal commission</div>
              <div className="text-xs text-slate-500">Each member's own commission in this bucket (not your override)</div>
            </div>
            <div className="text-sm font-bold text-bardin-ink">{fmtUSD(view.teamTotal)}</div>
          </summary>
          <div className="border-t border-slate-100">
            {view.teamRows.length === 0 ? (
              <Empty>No team commissions in this bucket.</Empty>
            ) : (
              <ul className="divide-y divide-slate-100">
                {view.teamRows.map((r) => (
                  <li key={r.id} className="px-4 py-2.5 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-sm text-bardin-ink">{r.name}</span>
                      {r.role === "team_lead" && <span className="ml-1.5 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">TL</span>}
                      <span className="ml-2 text-xs text-slate-400">{fmtInt(r.deals)} deal{r.deals === 1 ? "" : "s"}</span>
                    </div>
                    <span className="text-sm font-semibold text-bardin-ink shrink-0">{fmtUSD(r.personal)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Section({
  title,
  subtitle,
  total,
  children,
}: {
  title: string;
  subtitle: string;
  total: number;
  children: ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-4 overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
        <div>
          <div className="text-sm font-semibold text-bardin-ink">{title}</div>
          <div className="text-xs text-slate-500">{subtitle}</div>
        </div>
        <div className="text-base font-bold text-bardin-ink">{fmtUSD(total)}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function TypeGroupBlock({
  group,
  members,
  showOwner,
  sentToBooks,
}: {
  group: CommTypeGroup;
  members: Record<string, TeamMember>;
  showOwner: boolean;
  /** Oct 8, 2026 — whether a deal's line is on a live Books bill (Approved bucket). */
  sentToBooks?: (dealId: string) => boolean;
}) {
  return (
    <div className="px-4 py-2">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
        <span className="uppercase tracking-wide font-medium">{group.label}</span>
        <span className="font-semibold text-slate-700">{fmtUSD(group.total)}</span>
      </div>
      <ul className="divide-y divide-slate-50">
        {group.items.map((item, idx) => (
          <CommissionLine
            key={item.deal.id + ":" + idx}
            item={item}
            ownerName={showOwner && item.deal.owner_id ? members[item.deal.owner_id]?.full_name : null}
            sentToBooks={sentToBooks ? sentToBooks(item.deal.id) : false}
          />
        ))}
      </ul>
    </div>
  );
}

// One deal's line + its breakdown: rev × rate = gross, minus any fees = net.
// The formula line is hidden when it wouldn't reconcile (retail / explicit
// Chad-set commission), in which case we just show "Gross" + fees.
function CommissionLine({ item, ownerName, sentToBooks = false }: { item: CommLineItem; ownerName?: string | null; sentToBooks?: boolean }) {
  const d = item.deal;
  return (
    <li className="py-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm text-bardin-ink truncate">
            <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
            {sentToBooks && (
              <span
                title="Approved — the bill is in Books; it is paid from there"
                className="ml-1.5 align-middle inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200"
              >
                sent to Books for payment
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400 truncate">
            {d.property_street ?? "—"}
            {ownerName ? ` · RM: ${firstName(ownerName)}` : ""}
          </div>
        </div>
        <span className="text-sm font-medium text-bardin-ink shrink-0">{fmtUSD(item.amount)}</span>
      </div>
      <PaidProvenanceChip item={item} className="mt-0.5" />
      <Breakdown item={item} />
    </li>
  );
}

// rev × rate = gross, minus itemized fees = net. Formula hidden when it wouldn't
// reconcile (retail / explicit Chad-set commission) — then "Gross $X" + fees.
function Breakdown({ item }: { item: CommLineItem }) {
  const formula = item.rate > 0 && Math.abs(item.rev * item.rate - item.gross) < 0.5;
  const hasFees = item.fees.length > 0;
  if (!formula && !hasFees) return null;
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
      {formula ? (
        <span>{fmtUSD(item.rev)} × {(item.rate * 100).toFixed(0)}% = {fmtUSD(item.gross)}</span>
      ) : (
        <span>Gross {fmtUSD(item.gross)}</span>
      )}
      {item.fees.map((f, i) => (
        <span key={i} className="text-rose-600">− {f.label} {fmtUSD(f.amount)}</span>
      ))}
      {hasFees && <span className="font-medium text-slate-700">= {fmtUSD(item.amount)} net</span>}
    </div>
  );
}

// Exec/admin card — one deal, every commission line (RM / TL / ASM) + fees.
function ExecDealCard({
  entry,
}: {
  entry: { deal: CommissionDeal; total: number; lines: { personId: string; personName: string; item: CommLineItem }[] };
}) {
  const d = entry.deal;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-3 overflow-hidden">
      <div className="px-4 py-2.5 flex items-center justify-between gap-2 border-b border-slate-100">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-bardin-ink truncate">
            <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
          </div>
          <div className="text-xs text-slate-400 truncate">
            {d.property_street ?? "—"}
            {d.claim_number ? ` · #${d.claim_number}` : ""}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Total comm</div>
          <div className="text-base font-bold text-bardin-ink">{fmtUSD(entry.total)}</div>
        </div>
      </div>
      <ul className="divide-y divide-slate-50 px-4 py-1">
        {entry.lines.map((ln, i) => (
          <li key={ln.personId + ":" + ln.item.type + ":" + i} className="py-1.5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex items-center flex-wrap gap-x-2">
                <span className={"text-[10px] font-semibold px-1.5 py-0.5 rounded " + COMM_TYPE_COLORS[ln.item.type]}>
                  {COMM_TYPE_LABELS[ln.item.type]}
                </span>
                <span className="text-sm text-bardin-ink">{ln.personName}</span>
              </div>
              <span className="text-sm font-medium text-bardin-ink shrink-0">{fmtUSD(ln.item.amount)}</span>
            </div>
            <PaidProvenanceChip item={ln.item} className="mt-0.5" />
            <Breakdown item={ln.item} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-6 text-center text-sm text-slate-400">{children}</div>;
}
